import * as I from 'immutable';
import React from 'react';

import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {limitedParallelAll} from 'app/stores/RasterCalculationStore';
import {makeImageApiRequest} from 'app/utils/apiUtils';
import * as domUtils from 'app/utils/domUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';

import {MAX_FRAMES, TimelineFrame, getFrameMaxSize, getFrameUrl, getTimelineFrames} from './frames';
import {getDefaultRange} from './range';

const MAX_PARALLEL_REQUESTS = 12;
export const BASE_FRAME_MS = 500;

// null means the thumbnail is missing.
export type FrameImages = Record<string, HTMLImageElement | null>;

export interface ImageryTimelineState {
  layerKey: string;
  frames: TimelineFrame[];
  images: FrameImages;
  hasTooManyFrames: boolean;
  frameIndex: number;
  setFrameIndex: (i: number) => void;
  isPlaying: boolean;
  setIsPlaying: (p: boolean) => void;
  isBuffering: boolean;
  // The nearest loaded frame at or before frameIndex, so gaps hold the last image.
  displayed: {frame: TimelineFrame; image: HTMLImageElement} | null;
}

export function getFrameIndex(
  frames: TimelineFrame[],
  selected: {cursor: string; time: number} | null
): number {
  if (!selected || !frames.length) {
    return 0;
  }
  const exact = frames.findIndex((f) => f.cursor === selected.cursor);
  if (exact !== -1) {
    return exact;
  }
  let nearest = 0;
  frames.forEach((f, i) => {
    if (Math.abs(+f.date - selected.time) < Math.abs(+frames[nearest].date - selected.time)) {
      nearest = i;
    }
  });
  return nearest;
}

export function useImageryTimeline({
  isOpen,
  featureData,
  layerKey,
  map,
}: {
  isOpen: boolean;
  featureData: I.ImmutableOf<ApiFeatureData[]> | null;
  layerKey: string;
  map: mapboxgl.Map | null;
}): ImageryTimelineState {
  // One session per open timeline and layer. Frame size, loaded images and
  // playback carry across feature data refreshes within a session.
  const sessionDeps = [isOpen, layerKey];

  const scenes = React.useMemo(
    () => (isOpen && featureData ? getTimelineFrames(featureData, layerKey, false) : []),
    [isOpen, featureData, layerKey]
  );

  const {frames, hasTooManyFrames} = React.useMemo(() => {
    const range = getDefaultRange(scenes.map((s) => s.date));
    const inRange = range
      ? scenes.filter((s) => range.start <= +s.date && +s.date < range.end)
      : [];
    return inRange.length > MAX_FRAMES
      ? {frames: [], hasTooManyFrames: true}
      : {frames: inRange, hasTooManyFrames: false};
  }, [scenes]);

  // Sized once per session so that zooming doesn't refetch every frame.
  const firstBounds = frames[0]?.bounds;
  const maxSize = React.useMemo(
    () => (map && firstBounds ? getFrameMaxSize(map, firstBounds) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, layerKey, map, !!firstBounds]
  );

  const requestUrls = React.useMemo(
    () => (maxSize ? frames.map((f) => getFrameUrl(f.url, maxSize)) : []),
    [frames, maxSize]
  );

  // Keyed by request URL, so reloading the frame list only fetches new scenes.
  const [cache, setCache] = useStateWithDeps<FrameImages>({}, sessionDeps);
  const cacheRef = React.useRef<FrameImages>({});
  const objectUrlsRef = React.useRef<string[]>([]);

  React.useEffect(() => {
    const objectUrls: string[] = [];
    cacheRef.current = {};
    objectUrlsRef.current = objectUrls;
    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, sessionDeps);

  React.useEffect(() => {
    const abortController = new AbortController();

    limitedParallelAll(
      requestUrls
        .filter((url) => !(url in cacheRef.current))
        .map((url) => async () => {
          if (abortController.signal.aborted) {
            return;
          }
          let image: HTMLImageElement | null = null;
          try {
            image = await makeImageApiRequest(url, abortController.signal);
          } catch (e) {
            if (domUtils.isAbortError(e)) {
              return;
            }
          }
          // The image load isn't abortable, so it can land after a reload.
          if (abortController.signal.aborted) {
            if (image) {
              URL.revokeObjectURL(image.src);
            }
            return;
          }
          if (image) {
            objectUrlsRef.current.push(image.src);
          }
          cacheRef.current[url] = image;
          setCache((prev) => ({...prev, [url]: image}));
        }),
      MAX_PARALLEL_REQUESTS
    );

    return () => abortController.abort();
  }, [requestUrls, setCache]);

  const images = React.useMemo<FrameImages>(() => {
    const byCursor: FrameImages = {};
    frames.forEach((f, i) => {
      if (requestUrls[i] in cache) {
        byCursor[f.cursor] = cache[requestUrls[i]];
      }
    });
    return byCursor;
  }, [frames, requestUrls, cache]);

  // Held as a scene rather than an index so that a changed frame list keeps the
  // same scene, or the nearest one when it's no longer a frame.
  const [selected, setSelected] = useStateWithDeps<{cursor: string; time: number} | null>(
    null,
    sessionDeps
  );
  const frameIndex = getFrameIndex(frames, selected);
  const setFrameIndex = React.useCallback(
    (i: number) => {
      const frame = frames[i];
      if (frame) {
        setSelected({cursor: frame.cursor, time: +frame.date});
      }
    },
    [frames, setSelected]
  );

  const [wantsToPlay, setIsPlaying] = useStateWithDeps(false, sessionDeps);

  // Missing thumbnails are skipped rather than shown as a held frame.
  let nextIndex = frameIndex;
  for (let step = 1; step <= frames.length; step++) {
    const i = (frameIndex + step) % frames.length;
    if (images[frames[i].cursor] !== null) {
      nextIndex = i;
      break;
    }
  }
  const isPlaying = wantsToPlay && nextIndex !== frameIndex;
  const isBuffering = isPlaying && images[frames[nextIndex].cursor] === undefined;

  React.useEffect(() => {
    if (!isPlaying || isBuffering) {
      return;
    }
    const timeout = setTimeout(() => setFrameIndex(nextIndex), BASE_FRAME_MS);
    return () => clearTimeout(timeout);
  }, [isPlaying, isBuffering, nextIndex, setFrameIndex]);

  let displayed: ImageryTimelineState['displayed'] = null;
  for (let i = Math.min(frameIndex, frames.length - 1); i >= 0; i--) {
    const image = images[frames[i].cursor];
    if (image) {
      displayed = {frame: frames[i], image};
      break;
    }
  }

  return {
    layerKey,
    frames,
    images,
    hasTooManyFrames,
    frameIndex,
    setFrameIndex,
    isPlaying,
    setIsPlaying,
    isBuffering,
    displayed,
  };
}
